import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import "../styles/auth.css";

export function LeftPanel() {
  return (
    <div className="auth-left">
      <div className="auth-logo">
        <div className="auth-logo-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="#0E0F0D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
            <rect x="1" y="3" width="15" height="13" rx="2"/>
            <path d="M16 8h4l3 5v3h-7V8z"/>
            <circle cx="5.5" cy="18.5" r="2.5"/>
            <circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
        </div>
        <div>
          <div className="auth-logo-name">DepannRoute</div>
          <div className="auth-logo-tag">Plateforme certifiée de transport</div>
        </div>
      </div>

      <div className="auth-hero">
        <h1>Transport de véhicules,<br/><em>zéro trajet</em><br/>à vide.</h1>
        <p>La plateforme qui connecte particuliers et transporteurs professionnels certifiés, avec matching aller-retour intelligent.</p>
        <div className="auth-pills">
          <div className="auth-pill"><span className="auth-pill-dot"/>500+ transporteurs vérifiés</div>
          <div className="auth-pill"><span className="auth-pill-dot"/>Séquestre Stripe sécurisé</div>
          <div className="auth-pill"><span className="auth-pill-dot"/>Assurance RC Pro incluse</div>
        </div>
        <div className="auth-blockchain">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2" strokeLinecap="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <div className="auth-blockchain-text">
            <strong>Preuves photo scellées SHA-256</strong>
            Chaque étape documentée, chaque pièce horodatée
          </div>
        </div>
      </div>

      <div className="auth-road">
        <svg viewBox="0 0 500 300" preserveAspectRatio="xMidYMax slice" fill="none">
          <path d="M0 280 Q125 200 250 220 Q375 240 500 160" stroke="rgba(232,160,32,.15)" strokeWidth="60"/>
          <path d="M0 280 Q125 200 250 220 Q375 240 500 160" stroke="rgba(232,160,32,.06)" strokeWidth="120"/>
          <path d="M0 280 Q125 200 250 220 Q375 240 500 160" stroke="rgba(232,160,32,.3)" strokeWidth="1.5" strokeDasharray="18 12"/>
        </svg>
      </div>
    </div>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("client");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pwdVisible, setPwdVisible] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const redirectTo = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="auth-layout" data-testid="login-page">
      <LeftPanel />
      <div className="auth-right">
        <div className="auth-form-wrap">

          {error && (
            <div className="auth-alert auth-alert-error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <div className="auth-tabs">
            <button className={`auth-tab${tab === "client" ? " auth-tab-active" : ""}`} onClick={() => setTab("client")}>
              <span className="auth-tab-icon">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </span>
              Client particulier
            </button>
            <button className={`auth-tab${tab === "pro" ? " auth-tab-active" : ""}`} onClick={() => setTab("pro")}>
              <span className="auth-tab-icon">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              </span>
              Transporteur pro
            </button>
          </div>

          <div className="auth-pane" key={tab}>
            {tab === "client" ? (
              <>
                <h2 className="auth-title">Bon retour</h2>
                <p className="auth-subtitle">Connectez-vous pour suivre vos transports en cours et accéder à votre espace client.</p>
              </>
            ) : (
              <>
                <h2 className="auth-title">Espace professionnel</h2>
                <p className="auth-subtitle">Accédez à votre tableau de bord, missions disponibles et gestion documentaire.</p>
                <div className="auth-pro-notice">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  Vos documents (LTI, assurances…) sont vérifiés par notre équipe depuis votre espace Conformité.
                </div>
              </>
            )}
          </div>

          <form onSubmit={submit} data-testid="login-form">
            <div className="auth-field-group">
              <label className="auth-field-label" htmlFor="email">Adresse email</label>
              <div className="auth-field-wrap">
                <input type="email" id="email" placeholder="votre@email.com" autoComplete="email" required
                  value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email" />
                <div className="auth-field-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="auth-field-group">
              <label className="auth-field-label" htmlFor="password">Mot de passe</label>
              <div className="auth-field-wrap">
                <input type={pwdVisible ? "text" : "password"} id="password" placeholder="••••••••••"
                  autoComplete="current-password" required
                  value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password" />
                <div className="auth-field-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <button type="button" className="auth-pwd-toggle" onClick={() => setPwdVisible(v => !v)}>
                  {pwdVisible
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>

            <div className="auth-options-row">
              <Link to="/forgot-password" className="auth-forgot">Mot de passe oublié ?</Link>
            </div>

            <button type="submit" className="auth-btn-submit" disabled={loading} data-testid="login-submit">
              {loading ? (
                <><span className="auth-spinner"/><span>Connexion…</span></>
              ) : (
                <>
                  <span>Se connecter</span>
                  <div className="auth-btn-arrow">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </>
              )}
            </button>
          </form>

          <p className="auth-bottom-link">
            Pas encore de compte ? <Link to="/register" data-testid="link-register">Créer un compte →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
